import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert vibe coding coach evaluating a learner's first tool build.

Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

MESSAGE 1 QUALITY: Does the first message contain all six elements: what it does, who uses it, inputs, outputs, format, and visual standard? (1-4)

ITERATION DISCIPLINE: Was one change made per message throughout the build? (1-4)

PROTOTYPE FUNCTIONALITY: Does the final tool run independently and produce production-quality output? (1-4)

EVALUATION HONESTY: Was the prototype genuinely tested against each criterion? (1-4)

DAY 10 BRIEF: Is the next feature described with enough specificity to build from? (1-4)

After scoring:
1. Give a total score out of 20.
2. What's the single highest-value improvement to Message 1, the change that would have produced a significantly better first output?
3. Is the Day 10 brief specific enough to build from, or does it need more detail?
4. Name one use case where this tool would save the most time at work.

Here is my build:

[PASTE YOUR MESSAGE 1, FULL BUILD CONVERSATION LOG, AND EVALUATION RESULTS]`;

const RUBRIC_ROWS = [
  { criterion: 'MESSAGE 1 QUALITY', missing: 'Vague or missing key details', partial: 'Has what/who but not input/output specifics', solid: 'Inputs, outputs, and format all specified', excellent: 'Complete brief including visual standard and specific output format' },
  { criterion: 'ITERATION DISCIPLINE', missing: 'Multiple changes per message', partial: '2 changes in one message', solid: 'One change per message, mostly', excellent: 'Strictly one change per message with clear reasoning each time' },
  { criterion: 'PROTOTYPE FUNCTIONALITY', missing: "Tool doesn't run or errors immediately", partial: 'Runs but output is not usable', solid: 'Runs and produces useful output', excellent: 'Runs independently, output is production-quality' },
  { criterion: 'EVALUATION HONESTY', missing: 'No checklist completed', partial: 'Checked all boxes without testing', solid: 'Genuinely evaluated each criterion', excellent: 'Honest assessment with specific gaps identified for Day 10' },
  { criterion: 'DAY 10 BRIEF', missing: 'No next feature identified', partial: 'Vague feature idea', solid: 'Specific feature described', excellent: 'Feature described with same detail as Message 1, ready to build' },
];

const PREWORK_FIELDS = [
  { key: 'pw_does', name: 'What It Does', hint: 'One sentence. Describe the core function: not the value it adds, the thing it actually does.', height: 60 },
  { key: 'pw_who', name: 'Who Uses It', hint: 'Who is the primary user? Their role, their context, their level of AI familiarity.', height: 60 },
  { key: 'pw_input', name: 'What They Input', hint: 'What information does the user provide? List every field or input they need to enter.', height: 70 },
  { key: 'pw_produces', name: 'What It Produces', hint: 'What does the tool output? Be specific: a score, a formatted text, a checklist, a table?', height: 60 },
  { key: 'pw_visual', name: 'Visual Standard', hint: 'What should it look like? Professional, clean, branded to a color scheme? Simple or detailed?', height: 60 },
];

const EVAL_ITEMS = [
  'It runs independently in a browser, no internet connection required.',
  'It does exactly what Message 1 described: the output matches the specification.',
  'A colleague could use it with no instructions: the interface explains itself.',
  'The output is production-quality, something I could use at work right now.',
  'I know what the next feature adds: I have a specific Day 10 brief.',
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60, placeholder = '' }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay9({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day9'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day9: { ...(existing.progress?.day9 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day9: { ...(existing.progress?.day9 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day9: { ...(existing.progress?.day9 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rubricTotal = calcTotal(data, ['score_msg1', 'score_iteration', 'score_proto', 'score_eval', 'score_brief']);

  const MSG_BLOCKS = [
    {
      tag: 'MSG 1', key: 'msg1', title: 'Describe the Tool',
      desc: 'Include what it does, who uses it, what they input, what it produces, the output format, and the visual standard.',
      msgHeight: 100, responseLabel: 'What came back: does it match the description?', responseHeight: 66,
    },
    {
      tag: 'MSG 2', key: 'msg2', title: 'Test and Identify',
      desc: 'Name the one most important thing that doesn\'t work. Be specific: not "make it better" but "change X to Y because Z."',
      msgHeight: 80, responseLabel: 'What changed after this message?', responseHeight: 60,
    },
    {
      tag: 'MSG 3', key: 'msg3', title: 'Refine One Thing',
      desc: 'One specific change per message. Add MSG 3b, 3c, etc. if you need more iterations.',
      msgHeight: 80, responseLabel: 'What changed? Is the tool ready or does it need another iteration?', responseHeight: 60,
    },
    {
      tag: 'MSG 4', key: 'msg4', title: 'Finalize and Save',
      desc: '"Give me the complete final HTML as a single file with all changes incorporated, ready to save and open in a browser."',
      msgHeight: 60, responseLabel: null, responseHeight: 0,
    },
  ];

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 9 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={9} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 9</div>
        <div style={s.headerTitle}>Vibe Coding Part 1</div>
        <div style={s.headerDesc}>Describe it. Build it. Test it. One change at a time.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          Vibe coding is building functional software by describing what you want in plain language: AI writes the code, you focus on what the tool should do. The build conversation has four messages: describe the tool (specific inputs, outputs, format), test and identify the one most important change, refine one thing at a time, then get the complete final code as a single clean file. Good prototypes do one thing well, run independently in a browser, require no instructions to use, and produce output you could actually use at work right now. A working prototype today is the goal, not a finished product.
        </div>
      </div>

      <Section title="Pre-Work — Your Tool Idea">
        <div style={s.exerciseIntro}>Before you write a single message, answer these questions. Specificity here determines the quality of your first output. Vague brief, generic tool. Specific brief, useful tool.</div>
        <div style={s.prewTable}>
          {PREWORK_FIELDS.map((field, i) => (
            <div key={field.key} style={{ ...s.prewRow, borderBottom: '1px solid #E8E6E2' }}>
              <div style={s.prewLabel}>
                <div style={s.prewLabelName}>{field.name}</div>
                <div style={s.prewLabelHint}>{field.hint}</div>
              </div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: field.height, background: '#FAF8F5' }}
                value={data[field.key] || ''}
                onChange={e => set(field.key, e.target.value)}
              />
            </div>
          ))}
          <div style={{ ...s.prewRow, borderBottom: 'none' }}>
            <div style={s.prewLabel}>
              <div style={s.prewLabelName}>Output Format</div>
              <div style={s.prewLabelHint}>Single-page HTML / Multi-section HTML / Form with results / Interactive calculator</div>
            </div>
            <div style={{ flex: 1, padding: '12px', background: '#FAF8F5', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
              {['Single-page HTML', 'Multi-section HTML', 'Form with results', 'Interactive calculator'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3D3D3D', cursor: 'pointer' }}>
                  <input type="radio" name="output_format" checked={data.output_format === opt} onChange={() => set('output_format', opt)} /> {opt}
                </label>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Build Conversation Log">
        <div style={s.exerciseIntro}>Write each message in the book before you paste it into your AI tool. Writing first forces you to be specific. Log what came back after each message.</div>

        {MSG_BLOCKS.map((msg, i) => (
          <div key={msg.key} style={{ ...s.msgBlock, marginBottom: i < MSG_BLOCKS.length - 1 ? '14px' : '0' }}>
            <div style={s.msgHeader}>
              <div style={s.msgTag}>{msg.tag}</div>
              <div>
                <div style={s.msgTitle}>{msg.title}</div>
                <div style={s.msgDesc}>{msg.desc}</div>
              </div>
            </div>
            <div style={{ ...s.msgField, borderBottom: msg.key === 'msg4' ? 'none' : '1px solid #E8E6E2' }}>
              <div style={s.fieldLabel}>My message:</div>
              <textarea style={{ ...s.textarea, minHeight: msg.msgHeight }} value={data[`${msg.key}_msg`] || ''} onChange={e => set(`${msg.key}_msg`, e.target.value)} />
            </div>
            {msg.responseLabel && (
              <div style={{ ...s.msgField, borderBottom: 'none' }}>
                <div style={s.fieldLabel}>{msg.responseLabel}</div>
                <textarea style={{ ...s.textarea, minHeight: msg.responseHeight }} value={data[`${msg.key}_response`] || ''} onChange={e => set(`${msg.key}_response`, e.target.value)} />
              </div>
            )}
            {msg.key === 'msg4' && (
              <div style={{ ...s.msgField, borderBottom: 'none' }}>
                <div style={s.fieldLabel}>Confirmed: tool opens in browser independently, no errors?</div>
                <div style={{ display: 'flex', gap: '20px', margin: '8px 0' }}>
                  {['Yes', 'No — what is wrong?'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3D3D3D', cursor: 'pointer' }}>
                      <input type="radio" name="confirmed" checked={data.confirmed === opt} onChange={() => set('confirmed', opt)} /> {opt}
                    </label>
                  ))}
                </div>
                {data.confirmed === 'No — what is wrong?' && (
                  <textarea style={{ ...s.textarea, minHeight: 44 }} placeholder="Describe what's wrong..." value={data.confirmed_notes || ''} onChange={e => set('confirmed_notes', e.target.value)} />
                )}
              </div>
            )}
          </div>
        ))}
      </Section>

      <Section title="Prototype Evaluation">
        <div style={s.exerciseIntro}>Check each criterion against your final tool. Any unchecked box is an iteration for Day 10.</div>
        <div style={s.checklist}>
          {EVAL_ITEMS.map((item, i) => (
            <div key={i} style={{ ...s.checkItem, borderBottom: i < EVAL_ITEMS.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <input type="checkbox" style={{ marginTop: '2px', flexShrink: 0 }} checked={data[`eval_${i}`] || false} onChange={e => set(`eval_${i}`, e.target.checked)} />
              <span style={{ fontSize: '13px', color: '#3D3D3D', lineHeight: '1.6' }}>{item}</span>
            </div>
          ))}
        </div>
        <Field label="Any unchecked: what specifically is missing or broken?" value={data.eval_notes} onChange={v => set('eval_notes', v)} minHeight={60} />
      </Section>

      <Section title="Day 10 Brief — Your Next Feature">
        <div style={s.exerciseIntro}>The one addition that would make this tool significantly more useful. Write it with the same specificity as your Message 1.</div>
        <div style={s.d10Block}>
          <div style={{ ...s.d10Field, borderBottom: '1px solid #E8E6E2' }}>
            <div style={s.fieldLabel}>Current tool does:</div>
            <textarea style={{ ...s.textarea, minHeight: 50 }} value={data.d10_current || ''} onChange={e => set('d10_current', e.target.value)} />
          </div>
          <div style={{ ...s.d10Field, borderBottom: '1px solid #E8E6E2' }}>
            <div style={s.fieldLabel}>The next feature is:</div>
            <textarea style={{ ...s.textarea, minHeight: 60 }} value={data.d10_feature || ''} onChange={e => set('d10_feature', e.target.value)} />
          </div>
          <div style={{ ...s.d10Field, borderBottom: 'none' }}>
            <div style={s.fieldLabel}>My Day 10 Message 1 will say:</div>
            <textarea style={{ ...s.textarea, minHeight: 80 }} value={data.d10_msg1 || ''} onChange={e => set('d10_msg1', e.target.value)} />
          </div>
        </div>
      </Section>

      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Use these criteria to evaluate your Day 9 prototype.
        </div>
        <RubricTable rows={RUBRIC_ROWS} />
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your prototype:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Msg 1" value={data.score_msg1} onChange={v => set('score_msg1', v)} />
          <ScoreField label="Iteration" value={data.score_iteration} onChange={v => set('score_iteration', v)} />
          <ScoreField label="Prototype" value={data.score_proto} onChange={v => set('score_proto', v)} />
          <ScoreField label="Evaluation" value={data.score_eval} onChange={v => set('score_eval', v)} />
          <ScoreField label="Day 10 Brief" value={data.score_brief} onChange={v => set('score_brief', v)} />
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{rubricTotal} / 20</div>
          </div>
        </div>
      </Section>

      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this with your Message 1, your build conversation log, and your evaluation checklist.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      <Section title="Scores from AI Evaluation">
        <Field label="AI Score: ___ / 20     Highest-value Message 1 improvement:" value={data.ai_score} onChange={v => set('ai_score', v)} minHeight={66} />
      </Section>

      <CalloutBox title="THE STANDARD">
        Keep working until your prototype passes all five evaluation criteria and your Day 10 brief is specific enough to build from in the first message. If any criterion is unchecked, that's your iteration work before closing the book.
      </CalloutBox>

      <Section title="Reflection">
        <Field
          label="1. What surprised you most about the build process: either how fast something came together or how hard a specific part was to get right?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={80}
        />
        <Field
          label="2. Think about someone else on your team or in your organization. What tool could you build for them using what you learned today? What would their Message 1 look like?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      <Section title="Wins Tracker — Day 9">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <CalloutBox title="BEFORE DAY 10 — YOUR ASSIGNMENT">
        <span>Use your prototype on real work at least once. Then:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Note what the real use revealed that the session testing didn't catch.</li>
          <li>Confirm your Day 10 brief: is the next feature still the right one, or did real use reveal something more important?</li>
          <li>Refine your Day 10 Message 1 if needed. It should be as specific as your original Message 1.</li>
          <li>Come to Day 10 with your prototype file, your Day 10 brief, and one observation from real use.</li>
        </ol>
      </CalloutBox>
      <Field label="What real use revealed that testing didn't:" value={data.assignment_reveal} onChange={v => set('assignment_reveal', v)} minHeight={66} />

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 9 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 9 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 9 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '18px' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  prewTable: { border: '1.5px solid #E8E6E2' },
  prewRow: { display: 'flex' },
  prewLabel: { width: '150px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  prewLabelName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  prewLabelHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.4' },
  msgBlock: { border: '1.5px solid #E8E6E2' },
  msgHeader: { background: '#1B2A4A', padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: '12px' },
  msgTag: { background: '#D4A847', color: '#1B2A4A', fontSize: '10px', fontWeight: '700', padding: '3px 10px', letterSpacing: '1px', textTransform: 'uppercase', flexShrink: 0, marginTop: '1px' },
  msgTitle: { fontSize: '12px', fontWeight: '700', color: '#fff' },
  msgDesc: { fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginTop: '2px', lineHeight: '1.4' },
  msgField: { padding: '10px 14px' },
  checklist: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  checkItem: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px' },
  d10Block: { border: '1.5px solid #E8E6E2' },
  d10Field: { padding: '10px 14px' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
